import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AxiosResponse } from 'axios'

import {
  Transaction,
  TransactionError,
} from '@/entities/transactions/utils/types'
import { ExportOptions, FilterGroup, PaginatedFetch, PaginatedMeta, Sort } from '@types'

import { addStoreListItem, prepareResponseError, removeStoreListItem, updateStoreListItem } from '../utils/helpers'
import { prepareExportParams } from '@/helpers/common'

import { PAGE_SIZE } from '@/const/common'

import api, { getPaginatedRequest } from '@/store/api'
import useAnalyticsStore from '@/store/analytics'
import { useRepositoriesStore } from '@/store/repositories/index'

export const useTransactionsStore = defineStore('transactions', () => {
  // INIT
  const analyticsStore = useAnalyticsStore()
  const repositoriesStore = useRepositoriesStore()

  const list = ref<Transaction[]>([])
  const pagination = ref<PaginatedMeta>()

  const initFlag = ref(false)

  const loading = ref(true)
  const loadingAction = ref(false)

  const errors = ref<TransactionError[]>([])

  const search = ref<string>()
  const filter = ref<FilterGroup | null>()
  const sort = ref<Sort[] | null>()

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS
  const getList = computed(() => list.value)
  const getPagination = computed(() => pagination.value)
  const getErrors = computed(() => errors.value)

  const getSearch = computed(() => search.value)
  const getFilter = computed(() => filter.value)
  const getSort = computed(() => sort.value)

  // SETTERS
  const setList = (data: Transaction[], meta?: PaginatedMeta) => {
    list.value = data
    pagination.value = meta
  }

  const setErrors = (data: TransactionError[]) => {
    errors.value = data
  }

  const setOptions = (options: {
    search?: string
    filter?: FilterGroup | null
    sort?: Sort[] | null
  }) => {
    search.value = options.search || undefined
    filter.value = options.filter || null
    sort.value = options.sort || null
  }

  // ACTIONS

  const fetch: PaginatedFetch = async (
    options,
    store = true,
    signal?: AbortSignal,
    useStoreOptions = true,
    pageSize = PAGE_SIZE,
  ) => {
    if (!repositoryId.value) return
    if (store) loading.value = true
    try {
      const { data, meta, errors } = await getPaginatedRequest<Transaction>(
        'transactions',
        {
          repository_id: repositoryId.value,
          page_size: pageSize,
          ...(useStoreOptions ? {
            search: getSearch.value,
            filter: getFilter.value,
            sort: getSort.value,
          } : {}),
          ...options,
        },
        signal,
      )
      if (store) {
        setList(data, meta)
        setErrors(errors || [])
        initFlag.value = true
      }
      return { data, meta }
    } catch (e) {
      initFlag.value = true
      throw Error(prepareResponseError(e))
    } finally {
      if (store) loading.value = false
    }
  }

  const fetchSingle = async (transaction_id: string) => {
    if (!repositoryId.value) return
    try {
      const { data } = await api.get(`transactions/${transaction_id}`, {
        params: {
          repository_id: repositoryId.value,
        },
      })
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const fetchSummary = async (options: { asset_ids?: string }) => {
    try {
      const { data } = await api.get('transactions/summary', {
        params: {
          repository_id: repositoryId.value,
          ...options,
        },
      })

      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const exportList = async (options: ExportOptions) => {
    try {
      const params = prepareExportParams(options)
      const response = await api.get<Blob>('transactions/export', {
        params: {
          repository_id: repositoryId.value,
          ...params,
        },
      })
      return response.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const store = async (
    transaction: Transaction,
    analyticsDeprecated = true,
  ): Promise<Transaction | undefined> => {
    loadingAction.value = true
    try {
      const result = await api.post('transactions', {
        ...transaction,
        repository_id: repositoryId.value,
      })
      const data = result.data
      addStoreListItem<Transaction>(list.value, data)
      analyticsDeprecated && analyticsStore.markAsDeprecated()
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const update = async (
    transaction: Partial<Transaction>,
    analyticsDeprecated = true,
  ): Promise<Transaction | undefined> => {
    loadingAction.value = true
    try {
      const result = await api.put(
        `transactions/${transaction.id}`,
        transaction,
      )
      updateStoreListItem<Transaction>(list.value, result.data)
      analyticsDeprecated && analyticsStore.markAsDeprecated()
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const destroy = async (id: string, analyticsDeprecated = true): Promise<void> => {
    loadingAction.value = true
    try {
      await bulkDestroy([id])
      analyticsDeprecated && analyticsStore.markAsDeprecated()
    } catch (e) {
      throw Error(e as string)
    } finally {
      loadingAction.value = false
    }
  }

  const bulkStore = async (
    transactions: Partial<Transaction>[],
  ): Promise<Transaction[]> => {
    loadingAction.value = true
    try {
      const result = await api.post<Transaction[], AxiosResponse<Transaction[]>, Partial<Transaction>[]>(
        `transactions?repository_id=${repositoryId.value}`,
        transactions,
      )
      result.data.forEach(transaction => {
        addStoreListItem<Transaction>(list.value, transaction)
      })
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const bulkUpdate = async (
    transactions: Partial<Transaction>[],
  ): Promise<void> => {
    loadingAction.value = true
    try {
      await Promise.all(transactions.map(transaction => update(transaction, false)))
    } catch (e) {
      throw Error(e as string)
    } finally {
      loadingAction.value = false
    }
  }

  const bulkDestroy = async (ids: string[]): Promise<void> => {
    loadingAction.value = true
    try {
      await api.delete('transactions', { data: ids })
      ids.forEach(id => {
        removeStoreListItem<Transaction>(list.value, id)
      })
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const destroyAll = async (search: string | undefined, filter: FilterGroup | undefined, analyticsDeprecated = true): Promise<void> => {
    loadingAction.value = true
    try {
      const params = {
        repository_id: repositoryId.value,
        all_items: true,
        search,
        ...(filter ? { filter: JSON.stringify(filter) } : {}),
      }
      await api.delete('transactions', { params })
      clear()
      analyticsDeprecated && analyticsStore.markAsDeprecated()
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    initFlag.value = false
    setList([], undefined)
    setErrors([])
  }

  return {
    initFlag,
    loading,
    loadingAction,

    setOptions,

    getList,
    getPagination,
    getErrors,

    fetch,
    fetchSingle,
    fetchSummary,
    export: exportList,

    store,
    update,
    destroy,

    bulkStore,
    bulkUpdate,
    bulkDestroy,
    destroyAll,

    cancel,
    clear,
  }
})
